/**
 * Eleven v4 audio tags ("[reassuring]", "[sighs]") are delivery cues inside
 * spoken text. Strip them wherever that text is shown instead of heard.
 * Numeric citation markers such as "[1]" and markdown links are left alone.
 */
const DELIVERY_TAG = /[ \t]*\[[A-Za-zÀ-ɏ][A-Za-zÀ-ɏ ,'-]{1,48}\](?!\()[ \t]*/g;

/** Keep one space only when the removed cue sat between two words. */
function cueReplacement(match: string, offset: number, whole: string): string {
  const before = whole[offset - 1];
  const after = whole[offset + match.length];
  return before === undefined || after === undefined || /[\r\n]/.test(before) || /[\s.,!?;:)]/.test(after) ? "" : " ";
}

export function stripSpeechDeliveryTags(text: string): string {
  return text.replace(DELIVERY_TAG, cueReplacement).replace(/[ \t]{2,}/g, " ");
}

/**
 * The delivery tags the voice prompt allows in audioAnswer
 * (elevenV4AssistantAudioGuidelines builds its tag list from this). The model
 * may combine two, such as "[warm, reassuring]".
 */
export const ELEVEN_V4_DELIVERY_TAGS = [
  "warm", "reassuring", "sympathetic", "curious", "questioning",
  "thoughtful", "excited", "apologetic", "matter-of-fact", "sighs",
] as const;

const ALLOWED_TAGS: ReadonlySet<string> = new Set(ELEVEN_V4_DELIVERY_TAGS);

/**
 * A bracket that could be a delivery cue: letters in square brackets, joined
 * by spaces, hyphens, apostrophes or commas (Latin or CJK). It must stand
 * alone: not glued to a word or another bracket ("arr[index]", "[a][b]",
 * "[[wiki]]"), and not opening a markdown link, reference, or definition
 * ("[text](url)", "[text][ref]", "[ref]: url"). Whether it is removed is
 * decided per bracket by isDeliveryCue.
 */
const CUE_CANDIDATE = /[ \t]*(?<![\p{L}\p{N}_\]\\[])\[([\p{L}\p{M}][\p{L}\p{M} ,'’，、-]{1,48})\](?![(\[:\]])[ \t]*/gu;

/**
 * Code is never touched: fenced blocks (closed or running to the end), inline
 * spans, and indented code lines.
 */
const CODE_SPAN = /(```[\s\S]*?(?:```|(?![\s\S]))|`[^`\n]*`|^(?: {4}|\t)[^\n]*)/m;

function cueKey(inner: string): string {
  return inner.toLowerCase().split(/[,，、]/).map((part) => part.trim().replace(/[\s-]+/g, "-")).join(",");
}

function isAllowedTag(key: string): boolean {
  return key.split(",").every((part) => ALLOWED_TAGS.has(part));
}

/**
 * A cue sits where the prompt puts it: right before the words it affects, at
 * the start of the text, a line, or a sentence (or after another cue). This
 * keeps "Hi [your name]," and "he said [sic] that" as written.
 */
function atCuePosition(whole: string, offset: number, match: string): boolean {
  const before = whole.slice(0, offset).replace(/[ \t]+$/, "");
  const after = whole.slice(offset + match.length);
  const leads = before === "" || /[\n.!?。！？…\]]$/.test(before);
  const precedesWords = after === "" || /^[\p{L}\p{N}\[“"'¿¡]/u.test(after);
  return leads && precedesWords;
}

/** Cues the spoken script uses, as normalized keys. */
function spokenCueKeys(spoken: string): ReadonlySet<string> {
  const keys = new Set<string>();
  for (const segment of spoken.split(CODE_SPAN).filter((_, index) => index % 2 === 0)) {
    for (const match of segment.matchAll(CUE_CANDIDATE)) {
      if (atCuePosition(segment, match.index ?? 0, match[0])) keys.add(cueKey(match[1] ?? ""));
    }
  }
  return keys;
}

/**
 * Two signals decide that a bracket in shown text is a delivery cue:
 *
 * 1. It is a tag the prompt allows, in any letter case. Written as the prompt
 *    writes it (lowercase) it goes anywhere; another case ("[Warm]") only at a
 *    cue position, so "Set the mode to [Warm]." keeps its bracket.
 * 2. The same turn's distinct spoken script uses the same bracket as a cue, and
 *    both sit at a cue position. This catches tags the prompt does not list,
 *    including tags written in another language ("[温暖]"). Brackets with
 *    capitals never pass this signal: placeholders are written "[Your Name]".
 *
 * A verbatim copy of the text is no second signal, so it is ignored.
 */
function isDeliveryCue(inner: string, atCue: boolean, spokenKeys: ReadonlySet<string>): boolean {
  const key = cueKey(inner);
  if (isAllowedTag(key)) return atCue || inner === inner.toLowerCase();
  return atCue && inner === inner.toLowerCase() && spokenKeys.has(key);
}

/**
 * Removes Eleven v4 delivery cues from text meant to be read (textAnswer,
 * notifications, history). The result is stored, so it removes only brackets
 * that are surely cues (see isDeliveryCue): placeholders ("[your name]"),
 * "[sic]", "[a-z]", "[true, false]", "[[wiki]]", "Press [Enter]", checkboxes,
 * citations, links, code, and spacing away from a removed cue stay exactly as
 * written. Pass the same turn's audioAnswer as spokenText when there is one.
 */
export function stripDeliveryTagsFromDisplayText(text: string, spokenText?: unknown): string {
  if (!text.includes("[")) return text;
  const spokenKeys = typeof spokenText === "string" && spokenText.includes("[") && spokenText.trim() !== text.trim()
    ? spokenCueKeys(spokenText)
    : new Set<string>();
  return text
    .split(CODE_SPAN)
    .map((segment, index) => (index % 2 === 1 ? segment : removeCues(segment, spokenKeys)))
    .join("");
}

/** Removes the cues in one prose segment; touching cues go as one run. */
function removeCues(segment: string, spokenKeys: ReadonlySet<string>): string {
  const runs: Array<{ start: number; end: number }> = [];
  for (const match of segment.matchAll(CUE_CANDIDATE)) {
    const start = match.index ?? 0;
    if (!isDeliveryCue(match[1] ?? "", atCuePosition(segment, start, match[0]), spokenKeys)) continue;
    const end = start + match[0].length;
    const last = runs.at(-1);
    if (last?.end === start) last.end = end;
    else runs.push({ start, end });
  }
  let shown = "";
  let cursor = 0;
  for (const { start, end } of runs) {
    shown += segment.slice(cursor, start) + cueReplacement(segment.slice(start, end), start, segment);
    cursor = end;
  }
  return shown + segment.slice(cursor);
}

/**
 * A turn record whose shown text carries no delivery cues. Eleven v4 cues
 * belong to audioAnswer, which keeps them; only textAnswer is cleaned. The
 * same object comes back when there is nothing to remove.
 */
export function withDisplayTextAnswer<T>(turn: T): T {
  if (!turn || typeof turn !== "object") return turn;
  const { textAnswer, audioAnswer } = turn as { textAnswer?: unknown; audioAnswer?: unknown };
  if (typeof textAnswer !== "string") return turn;
  const shown = stripDeliveryTagsFromDisplayText(textAnswer, audioAnswer);
  return shown === textAnswer ? turn : { ...turn, textAnswer: shown };
}

/**
 * Which answer a spoken turn's caption shows. Absent: the spoken script, as
 * the voice said it. `"text"`: textAnswer, because audioAnswer only tells the
 * voice how to say it (an address spelled out letter by letter) and reads as
 * noise on screen.
 */
export type AnswerCaption = "text";

/**
 * The answer to show for a spoken turn. audioAnswer is often a verbatim copy
 * of textAnswer, whose brackets are real content, so only a distinct spoken
 * script is stripped with the broad speech grammar. A copy, or a turn with no
 * speech, loses only standalone delivery cues. A script that is nothing but
 * tags falls back to text, and a turn whose caption is its text shows that.
 */
export function displayedSpokenAnswer(audioAnswer: unknown, textAnswer: unknown, answerCaption?: unknown): string | undefined {
  const text = typeof textAnswer === "string" ? stripDeliveryTagsFromDisplayText(textAnswer, audioAnswer) : undefined;
  if (answerCaption === "text" && text?.trim()) return text;
  if (typeof audioAnswer !== "string" || !audioAnswer.trim()) return text;
  // Compare trimmed: callers trim either side, and a trailing newline must not
  // turn a verbatim copy into a distinct spoken script.
  if (typeof textAnswer === "string" && audioAnswer.trim() === textAnswer.trim()) return text;
  return stripSpeechDeliveryTags(audioAnswer).trim() || text;
}
